import React, { useRef, useEffect, useState, useContext } from 'react';
import { State } from '../../contexts/GlobalState';
import { createUseStyles } from 'react-jss';
import variables from '../../theme/variables';
import TransitionText from '../common/TransitionText';
import SecondaryCopy from './SecondaryCopy';
import SecondaryHeadline from './SecondaryHeadline';
import { ReactComponent as PlayIcon } from '../../assets/icons/ButtonPlay.svg';
import { ReactComponent as PauseIcon } from '../../assets/icons/ButtonPause.svg';

const useStyles = createUseStyles(
    {
        wrap: {
            height: '100%',
            display: 'flex',
            alignItems: 'flex-start',
        },

        frame: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            paddingBottom: 100,
            width: '100%',
        },

        imageWrap: {
            position: 'relative',
            marginBottom: 32,
            width: '100%',

            '@media (min-width: 1024px)': {
                height: '52vh',
            },
        },

        image: {
            display: 'block',
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'center',
            borderRadius: variables.borderRadius.xl,
        },

        controls: {
            position: 'absolute',
            bottom: 28,
            left: 28,
            right: 28,
            display: 'flex',
            alignItems: 'center',
        },

        playPauseButton: {
            marginRight: 16,
        },

        progress: {
            flex: 1,
            cursor: 'pointer',
        },

        progressBar: {
            height: 4,
            width: '100%',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
        },

        elapsedBar: {
            height: 4,
            width: 'var(--elapsed)',
            background: variables.palette.white,
            pointerEvents: 'none',
        },

        elapsedTime: {
            marginLeft: 16,
            width: 50,
        },
    },
    { name: 'SecondaryAudio' }
);

export default function SecondaryAudio({ hotspot, state }) {
    const classes = useStyles();
    const { muted, setSoundscapeMuted } = useContext(State);
    const audioRef = useRef();
    const [paused, setPaused] = useState(false);
    const [currentPercentage, setCurrentPercentage] = useState('0%');
    const [currentTime, setCurrentTime] = useState('0:00');

    useEffect(() => {
        setSoundscapeMuted(true);

        return () => {
            setSoundscapeMuted(false);
        };
    }, []);

    useEffect(() => {
        let interval;

        if (audioRef.current) {
            audioRef.current.addEventListener('play', () => setPaused(false));
            audioRef.current.addEventListener('pause', () => setPaused(true));

            interval = setInterval(checkTime, 330);
        }

        return () => {
            if (interval) {
                clearInterval(interval);
            }
        };
    }, [audioRef.current]);

    const togglePlay = () => {
        if (audioRef.current) {
            if (audioRef.current.paused) {
                audioRef.current.play();
            } else {
                audioRef.current.pause();
            }
        }
    };

    const checkTime = () => {
        if (audioRef.current) {
            const currentTime = audioRef.current.currentTime;
            const duration = audioRef.current.duration;
            const percentageNum = (currentTime / duration) * 100;
            setCurrentPercentage(percentageNum + '%');

            const minutes = Math.floor(currentTime / 60);
            const seconds = Math.floor(currentTime) % 60;
            setCurrentTime(`${minutes}:${seconds < 10 ? '0' : ''}${seconds}`);
        }
    };

    const skipTo = (e) => {
        const rect = e.target.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const w = rect.width;
        const percentage = x / w;

        if (audioRef.current) {
            audioRef.current.currentTime =
                audioRef.current.duration * percentage;
        }
    };

    return (
        <div className={classes.wrap}>
            <div className={classes.frame}>
                <div className={classes.imageWrap}>
                    <img
                        src={hotspot.content.image}
                        className={classes.image}
                    />
                    <audio
                        src={hotspot.content.audio}
                        autoPlay
                        loop
                        muted={muted}
                        ref={audioRef}
                    />
                    <div className={classes.controls}>
                        <button
                            className={classes.playPauseButton}
                            onClick={togglePlay}
                        >
                            {paused ? <PlayIcon /> : <PauseIcon />}
                        </button>
                        <div className={classes.progress} onClick={skipTo}>
                            <div className={classes.progressBar}>
                                <div
                                    className={classes.elapsedBar}
                                    style={{
                                        '--elapsed': currentPercentage,
                                    }}
                                ></div>
                            </div>
                        </div>
                        <div className={classes.elapsedTime}>{currentTime}</div>
                    </div>
                </div>
                <SecondaryHeadline
                    title={hotspot.content.title}
                ></SecondaryHeadline>
                <SecondaryCopy>{hotspot.content.copy}</SecondaryCopy>
            </div>
        </div>
    );
}
